import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, User, Mail, Building, Globe, Phone, Loader2, LogIn } from 'lucide-react';
import { extractDomainFromEmail, isFreeEmailDomain, guessCompanyName, guessWebsiteUrl } from '../utils/domainUtils';

/**
 * A simplified registration page with the glassy UI style that collects only name and email
 * Used for both London Book Fair and Summer Welcome dashboard flows
 */
const GlassyRegister = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentStep, setCurrentStep] = useState(0); // 0 = user info, 1 = org info
    const [suggestedOrgs, setSuggestedOrgs] = useState([]);
    const [suggestedWebsites, setSuggestedWebsites] = useState([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    
    // Form data state
    const [formData, setFormData] = useState({
        // User information
        name: '',
        email: '',
        phone: '+44 ', // Pre-populate with UK country code
        password: generateRandomPassword(), // Auto-generated password
        
        // Organization information
        orgName: '',
        website: '',
    });
    
    // Check for user data on component mount
    useEffect(() => {
        try {
            const userDataString = localStorage.getItem('userData');
            const userData = userDataString ? JSON.parse(userDataString) : null;
            const userStatus = userData && userData.id 
                ? `User logged in: ID ${userData.id}` 
                : 'No user logged in';
            console.log('User state on component mount:', userStatus);
        } catch (error) {
            console.error('Error checking user data:', error);
        }
    }, []);
    
    // Generate a random 6-letter password
    function generateRandomPassword() {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        let result = '';
        for (let i = 0; i < 6; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }
    
    // Form validation state
    const [isFormValid, setIsFormValid] = useState({
        userInfo: false,
        orgInfo: false
    });
    
    // Update form field handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    // Special handler for phone input to preserve country code
    const handlePhoneChange = (e) => {
        const value = e.target.value;
        
        // Check if the user is entering a different country code
        if (value.startsWith('+') && !value.startsWith('+44 ')) {
            // User is entering a different country code, allow it
            setFormData(prev => ({
                ...prev,
                phone: value
            }));
        } else if (!value.startsWith('+')) {
            // No country code at all, check if they're deleting or starting fresh
            if (value.length === 0) {
                // They've deleted everything, let them start fresh
                setFormData(prev => ({
                    ...prev,
                    phone: value
                }));
            } else {
                // They're typing without a country code, add the default
                setFormData(prev => ({
                    ...prev,
                    phone: '+44 ' + value
                }));
            }
        } else {
            // Normal case - just update the value
            setFormData(prev => ({
                ...prev,
                phone: value
            }));
        }
    };
    
    // Handle focus on phone input to place cursor at the end
    const handlePhoneFocus = (e) => {
        // Only place cursor at the end if the default country code is present
        if (e.target.value === '+44 ') {
            const length = e.target.value.length;
            e.target.setSelectionRange(length, length);
        }
    };
    
    // Validate the form
    const validateForm = () => {
        // Validate user information - name, email, and phone required
        const isNameValid = formData.name.trim().length >= 2;
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
        const isPhoneValid = formData.phone.trim().length >= 5; // Basic validation for phone
        
        // Validate organization information
        const isOrgNameValid = formData.orgName.trim().length >= 2;
        const isWebsiteValid = formData.website.trim().length > 0;
        
        setIsFormValid({
            userInfo: isNameValid && isEmailValid && isPhoneValid,
            orgInfo: isOrgNameValid && isWebsiteValid
        });
        
        return isNameValid && isEmailValid && isPhoneValid && isOrgNameValid && isWebsiteValid;
    };
    
    // Validate on form data change
    useEffect(() => {
        validateForm();
    }, [formData]);
    
    // Generate organization suggestions using OpenAI
    const generateOrgSuggestions = async (domain) => {
        try {
            setIsLoadingSuggestions(true);
            
            // Call the OpenAI API to get organization name suggestions
            const response = await fetch('/api/openai/org-suggestions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    domain,
                    email: formData.email,
                    name: formData.name
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('API error:', errorData);
                throw new Error(errorData.error || 'Failed to get organization suggestions');
            }
            
            const data = await response.json();
            console.log('Received organization suggestions:', data);
            
            // Ensure the domain from the email is always included in the website suggestions
            let websiteSuggestions = data.websites || [];
            
            // Clean up website URLs - remove any annotations like "(guess)" or "(if any)"
            websiteSuggestions = websiteSuggestions.map(website => {
                // Extract just the URL part, removing any annotations
                const urlMatch = website.match(/(https?:\/\/[^\s()]+)/);
                return urlMatch ? urlMatch[1] : website.split(' ')[0];
            });
            
            const emailDomainUrl = `https://www.${domain}`;
            
            // Check if the email domain is already in the suggestions
            const domainAlreadyIncluded = websiteSuggestions.some(website => 
                website.toLowerCase().includes(domain.toLowerCase())
            );
            
            // If not, add it as the first suggestion
            if (!domainAlreadyIncluded && domain) {
                websiteSuggestions = [emailDomainUrl, ...websiteSuggestions];
            }
            
            // Set the suggested organization names and websites
            setSuggestedOrgs(data.organizations || []);
            setSuggestedWebsites(websiteSuggestions);
            
            // Queue all suggested website domains for processing
            try {
                // Get user data from localStorage
                const userDataString = localStorage.getItem('userData');
                const userData = userDataString ? JSON.parse(userDataString) : null;
                
                if (userData && userData.id) {
                    console.log('GlassyRegister: Queueing suggested domains for processing');
                    
                    // Import the function to avoid circular dependencies if needed
                    const { queueDomainForProcessing } = await import('../services/backgroundJobService.js');
                    
                    // Process each website suggestion
                    for (const websiteUrl of websiteSuggestions) {
                        try {
                            // Extract domain from website URL
                            const url = new URL(websiteUrl);
                            const suggestedDomain = url.hostname.replace(/^www\./, '');
                            
                            // Skip if it's the same as the email domain (already processed)
                            if (suggestedDomain === domain) continue;
                            
                            // Queue the domain for processing
                            await queueDomainForProcessing(suggestedDomain, userData.id);
                            console.log('GlassyRegister: Queued suggested domain for processing:', suggestedDomain);
                        } catch (urlError) {
                            console.warn('GlassyRegister: Could not process suggested domain URL:', websiteUrl, urlError);
                        }
                    }
                }
            } catch (queueError) {
                console.error('GlassyRegister: Error queueing suggested domains:', queueError);
                // Don't fail if domain queuing fails
            }
            
            // If we have suggestions, pre-populate the first one
            if (data.organizations && data.organizations.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    orgName: data.organizations[0]
                }));
            }
            
            // Always set the website to the email domain first if available
            if (domain) {
                setFormData(prev => ({
                    ...prev,
                    website: emailDomainUrl
                }));
            } else if (websiteSuggestions.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    website: websiteSuggestions[0]
                }));
            }
        } catch (error) {
            console.error('Error generating organization suggestions:', error);
            // Fallback to basic domain-based guesses
            const companyName = guessCompanyName(domain);
            const websiteUrl = guessWebsiteUrl(domain);
            
            // Create variations of the company name for suggestions
            const orgSuggestions = [];
            if (companyName) {
                orgSuggestions.push(`${companyName} (Likely)`);
                orgSuggestions.push(`${companyName} Publishing (Guess)`);
                orgSuggestions.push(`${companyName} Books (Guess)`);
            }
            
            // Create variations of the website URL for suggestions
            const websiteSuggestions = [];
            // Always add the email domain as the first suggestion
            if (domain) {
                websiteSuggestions.push(`https://www.${domain}`);
            }
            
            if (websiteUrl && websiteUrl !== `https://www.${domain}`) {
                websiteSuggestions.push(websiteUrl);
                
                // Add variations like .org, .net if the original is .com
                if (websiteUrl.includes('.com')) {
                    websiteSuggestions.push(websiteUrl.replace('.com', '.org'));
                    websiteSuggestions.push(websiteUrl.replace('.com', '.net'));
                }
            }
            
            setSuggestedOrgs(orgSuggestions);
            setSuggestedWebsites(websiteSuggestions);
            
            if (orgSuggestions.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    orgName: orgSuggestions[0]
                }));
            }
            
            // Always default to the email domain website
            if (domain) {
                setFormData(prev => ({
                    ...prev,
                    website: `https://www.${domain}`
                }));
            } else if (websiteSuggestions.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    website: websiteSuggestions[0]
                }));
            }
        } finally {
            setIsLoadingSuggestions(false);
        }
    };
    
    // Process domain from email when moving to organization step
    const processDomainFromEmail = async () => {
        const domain = extractDomainFromEmail(formData.email);
        
        if (domain && !isFreeEmailDomain(domain)) {
            // Generate organization suggestions based on the domain
            await generateOrgSuggestions(domain);
        } else {
            // If it's a free email domain or no domain found, just move to the next step
            // without trying to generate suggestions
            setIsLoadingSuggestions(false);
        }
    };
    
    // Go back to home
    const goBack = () => {
        if (currentStep === 0) {
            navigate('/');
        } else {
            setCurrentStep(0);
        }
    };
    
    // Go to next step
    const goToNextStep = () => {
        if (currentStep === 0 && isFormValid.userInfo) {
            handleUserRegistration();
        } else if (currentStep === 1 && isFormValid.orgInfo) {
            handleOrgRegistration();
        }
    };
    
    // Handle user registration
    const handleUserRegistration = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            console.log('GlassyRegister: Submitting registration form...');
            
            // Call the actual registration API
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    password: formData.password
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Registration failed');
            }

            // Get the user data from the response
            const user = await response.json();
            console.log('GlassyRegister: Registration successful:', user);
            
            // Store user data in localStorage for authentication
            localStorage.setItem('userData', JSON.stringify(user));
            
            // Process email domain for brand information
            const emailDomain = extractDomainFromEmail(formData.email);
            if (emailDomain && !isFreeEmailDomain(emailDomain)) {
                try {
                    console.log('GlassyRegister: Queueing email domain for processing:', emailDomain);
                    // Import the function to avoid circular dependencies if needed
                    const { queueDomainForProcessing } = await import('../services/backgroundJobService.js');
                    const result = await queueDomainForProcessing(emailDomain, user.id);
                    console.log('GlassyRegister: Email domain queued for processing result:', result);
                } catch (queueError) {
                    console.error('GlassyRegister: Failed to queue email domain for processing:', queueError);
                    // Don't fail registration if domain queuing fails
                }
            } else {
                console.log('GlassyRegister: Not processing email domain - free email or no domain found:', emailDomain);
            }
            
            // Process domain from email to get organization suggestions
            await processDomainFromEmail();
            
            // Move to organization info step
            setCurrentStep(1);
        } catch (err) {
            setError(err.message || 'An error occurred during registration');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Handle organization registration
    const handleOrgRegistration = async () => {
        if (!validateForm()) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            // Get user ID from localStorage
            const userDataString = localStorage.getItem('userData');
            const userData = userDataString ? JSON.parse(userDataString) : null;
            
            if (!userData || !userData.id) {
                throw new Error('User data not available');
            }
            
            // Normalize the website URL
            let normalizedWebsiteUrl = formData.website;
            if (normalizedWebsiteUrl && !normalizedWebsiteUrl.startsWith('http')) {
                normalizedWebsiteUrl = 'https://' + normalizedWebsiteUrl;
            }
            
            // Extract domain from website URL for domain processing
            let domain = '';
            try {
                if (normalizedWebsiteUrl) {
                    const url = new URL(normalizedWebsiteUrl);
                    domain = url.hostname.replace(/^www\./, '');
                    console.log('Extracted domain for processing:', domain);
                }
            } catch (e) {
                console.warn('Could not parse URL for domain:', e);
            }
            
            // Prepare organization data
            const orgData = {
                name: formData.orgName,
                website_url: normalizedWebsiteUrl,
                phone_number: formData.phone || '',
                org_type: 'client',
                userId: userData.id
            };
            
            console.log('GlassyRegister: Creating organization with data:', orgData);
            
            // Add authorization header with user ID
            const headers = {
                'Content-Type': 'application/json',
                'X-User-ID': userData.id
            };
            
            // If there's an email, add it to the authorization
            if (userData.email) {
                const authToken = btoa(JSON.stringify({
                    id: userData.id,
                    email: userData.email,
                    timestamp: new Date().toISOString()
                }));
                headers['Authorization'] = `Bearer ${authToken}`;
                
                // Also store the auth token in localStorage for future API calls
                localStorage.setItem('authToken', authToken);
            }
            
            // Create the organization using the fixed API
            const response = await fetch('/api/organizations', {
                method: 'POST',
                headers: headers,
                credentials: 'include',
                body: JSON.stringify(orgData)
            });
            
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                console.error('Organization creation error:', data);
                throw new Error(data.error || data.message || 'Organization creation failed');
            }
            
            // Get the organization data from the response
            const organization = await response.json();
            console.log('GlassyRegister: Organization creation successful:', organization);
            
            // Store the organization in localStorage
            if (organization && organization.id) {
                localStorage.setItem('currentOrganization', JSON.stringify(organization));
                
                // Process the website domain explicitly
                if (domain) {
                    try {
                        console.log('GlassyRegister: Explicitly queueing website domain for processing:', domain);
                        // Import the function to avoid circular dependencies if needed
                        const { queueDomainForProcessing } = await import('../services/backgroundJobService.js');
                        const result = await queueDomainForProcessing(domain, userData.id, organization.id);
                        console.log('GlassyRegister: Website domain queued for processing result:', result);
                    } catch (queueError) {
                        console.error('GlassyRegister: Failed to queue website domain for processing - error details:', queueError);
                        // Don't fail organization creation if domain queuing fails
                    }
                } else {
                    console.log('GlassyRegister: No valid domain to process from website URL');
                }
            }
            
            // Check if we should redirect to the questionnaire (from LBF or Summer dashboard)
            const lbfRedirectIntent = localStorage.getItem('lbf_redirect_intent');
            const summerRedirectIntent = localStorage.getItem('summer_redirect_intent');
            localStorage.removeItem('lbf_redirect_intent'); // Clear the LBF intent
            localStorage.removeItem('summer_redirect_intent'); // Clear the Summer intent
            
            const redirectIntent = lbfRedirectIntent || summerRedirectIntent;
            const redirectDashboard = lbfRedirectIntent ? '/lbf' : '/summer-welcome';
            
            // Store the organization object in localStorage to ensure it's available on redirect
            if (organization) {
                localStorage.setItem('currentOrganization', JSON.stringify(organization));
            }
            
            // Update the session state to make sure we remain logged in
            if (userData) {
                // Create an authentication object that combines user and organization data
                const authState = {
                    user: userData,
                    organization: organization,
                    isAuthenticated: true,
                    timestamp: new Date().toISOString()
                };
                
                // Store in sessionStorage for immediate use
                sessionStorage.setItem('authState', JSON.stringify(authState));
                
                // Also make sure userData is correctly stored
                localStorage.setItem('userData', JSON.stringify(userData));
            }
            
            if (redirectIntent === 'questionnaire') {
                // Redirect back to the appropriate dashboard with a flag to show questionnaire
                navigate(redirectDashboard, { 
                    state: { 
                        showQuestionnaire: true,
                        newUser: true,
                        website: normalizedWebsiteUrl,
                        organizationData: organization, // Pass the organization directly in state
                        userData: userData // Also pass the user data
                    } 
                });
                return; // Exit early - don't reload the page
            } else if (redirectIntent === 'fullQuestionnaire') {
                // Redirect back to the appropriate dashboard with a flag to show full questionnaire
                navigate(redirectDashboard, { 
                    state: { 
                        showFullQuestionnaire: true,
                        newUser: true,
                        website: normalizedWebsiteUrl,
                        organizationData: organization, // Pass the organization directly in state
                        userData: userData // Also pass the user data
                    } 
                });
                return; // Exit early - don't reload the page
            } else if (redirectIntent === 'appWizard') {
                // Redirect directly to app wizard
                navigate('/app-wizard', { 
                    state: { 
                        newUser: true,
                        website: normalizedWebsiteUrl,
                        organizationData: organization,
                        userData: userData
                    } 
                });
                return; // Exit early - don't reload the page
            }
            
            // Otherwise continue to the app wizard
            navigate('/app-wizard', { 
                state: { 
                    newUser: true,
                    website: normalizedWebsiteUrl,
                    organizationName: formData.orgName
                } 
            });
            
            // Force a page reload only for non-questionnaire redirects
            if (!redirectIntent) {
                window.location.href = '/app-wizard';
            }
            
        } catch (err) {
            setError(err.message || 'An error occurred during organization creation');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Handle form submission (combined function)
    const handleSubmit = async () => {
        if (currentStep === 0) {
            await handleUserRegistration();
        } else {
            await handleOrgRegistration();
        }
    };
    
    // Handle clicking on a suggested organization name
    const handleOrgSuggestionClick = (orgName) => {
        setFormData(prev => ({
            ...prev,
            orgName
        }));
    };
    
    // Handle clicking on a suggested website
    const handleWebsiteSuggestionClick = (website) => {
        // Clean the website URL before setting it in the form
        const cleanWebsite = website.replace(/\s+\(.*?\)$/, '');
        setFormData(prev => ({
            ...prev,
            website: cleanWebsite
        }));
    };
    
    // Process domain from email when user enters a valid email and moves to the name field
    const handleEmailBlur = async (e) => {
        const email = e.target.value;
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            const domain = extractDomainFromEmail(email);
            if (domain && !isFreeEmailDomain(domain)) {
                try {
                    console.log('Valid email domain detected:', domain);
                    
                    // Store the domain for later reference
                    sessionStorage.setItem('pendingDomainToProcess', domain);
                    
                    // Try to queue immediately with user ID if available
                    const userDataString = localStorage.getItem('userData');
                    const userData = userDataString ? JSON.parse(userDataString) : null;
                    
                    if (userData && userData.id) {
                        console.log('Processing domain from email with existing user ID:', domain);
                        
                        // Import the function to avoid circular dependencies
                        const { queueDomainForProcessing } = await import('../services/backgroundJobService.js');
                        
                        // Queue the domain for processing
                        await queueDomainForProcessing(domain, userData.id);
                        console.log('Successfully queued domain for processing:', domain);
                    } else {
                        console.log('No user ID available - using public queue API for domain:', domain);
                        
                        // Use the public queue API when no user ID is available
                        try {
                            const response = await fetch('/api/domains/queue-public', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ domain })
                            });
                            
                            if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.message || 'Failed to queue domain via public API');
                            }
                            
                            const data = await response.json();
                            console.log('Successfully queued domain via public API:', data);
                        } catch (queueError) {
                            console.error('Error queueing domain via public API:', queueError);
                            // Don't fail form validation if API call fails
                        }
                    }
                } catch (error) {
                    console.error('Error processing domain from email:', error);
                    // Don't fail if domain queuing fails
                }
            }
        }
    };
    
    return (
        <div className="min-h-screen h-screen bg-green-800 text-white flex flex-col overflow-hidden">
            <div className="flex-1 p-2 sm:p-3 overflow-hidden">
                <div className="w-full max-w-[1400px] mx-auto h-full">
                    <div className="w-full max-w-[1400px] mx-auto h-[90vh] bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-xl flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="py-2 px-4 border-b border-white/10 bg-green-700/20">
                            <div className="relative flex items-center w-full justify-center py-3">
                                <h1 className="text-xl font-bold text-white">Registration</h1>
                                {currentStep === 0 && (
                                    <div className="absolute right-0">
                                        <button
                                            onClick={() => navigate('/login')}
                                            className="flex items-center px-3 py-1.5 rounded-md
                                                bg-white/20 text-white shadow-sm hover:bg-white/30
                                                transition-colors duration-200 text-sm font-medium"
                                        >
                                            <LogIn className="w-4 h-4 mr-1" />
                                            <span>Sign In</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Progress indicator */}
                        <div className="py-2 px-4 border-b border-white/10 bg-green-700/20">
                            <div className="relative flex items-center justify-center w-full">
                                {/* Progress bar container - 50% width and centered */}
                                <div className="relative w-1/2 flex items-center">
                                    {/* Progress bar background */}
                                    <div className="absolute h-1 bg-white/20 rounded-full w-full"></div>
                                    
                                    {/* Progress bar fill */}
                                    <div 
                                        className="absolute h-1 bg-yellow-300 rounded-full transition-all duration-300"
                                        style={{ width: `${currentStep * 100}%` }}
                                    ></div>
                                    
                                    {/* Step indicators */}
                                    <div className="relative w-full flex justify-between items-center py-3">
                                        <div className="flex flex-col items-center">
                                            <div 
                                                className={`w-3 h-3 rounded-full ${
                                                    currentStep === 0 ? 'bg-yellow-300 ring-2 ring-yellow-300/50 ring-offset-1 ring-offset-green-800' : 'bg-yellow-300'
                                                } transition-all duration-200`}
                                            ></div>
                                        </div>
                                        
                                        <div className="flex flex-col items-center">
                                            <div 
                                                className={`w-3 h-3 rounded-full ${
                                                    currentStep === 1 ? 'bg-yellow-300 ring-2 ring-yellow-300/50 ring-offset-1 ring-offset-green-800' : 'bg-white/30'
                                                } transition-all duration-200`}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Main content area with scrolling */}
                        <div className="flex-1 overflow-y-auto p-4 md:p-6">
                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-300 mb-4"></div>
                                    <p className="text-white text-lg">
                                        {currentStep === 0 ? 'Creating your account...' : 'Setting up your organization...'}
                                    </p>
                                </div>
                            ) : (
                                <div className="min-h-full flex flex-col">
                                    {/* Form content */}
                                    <div className="max-w-md mx-auto w-full">
                                        {currentStep === 0 ? (
                                            <div className="space-y-4">
                                                <h2 className="text-2xl font-bold text-white mb-6">Create Your Account</h2>
                                                
                                                <div className="space-y-4">
                                                    <div>
                                                        <label htmlFor="email" className="block text-white text-sm font-medium mb-1">Email Address</label>
                                                        <div className="relative">
                                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-700 w-5 h-5" />
                                                            <input
                                                                type="email"
                                                                id="email"
                                                                name="email"
                                                                value={formData.email}
                                                                onChange={handleInputChange}
                                                                onBlur={handleEmailBlur}
                                                                className="pl-10 pr-4 py-2 w-full bg-white rounded-md border border-green-700/20 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent text-gray-800"
                                                                placeholder="you@example.com"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div>
                                                        <label htmlFor="name" className="block text-white text-sm font-medium mb-1">Full Name</label>
                                                        <div className="relative">
                                                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-700 w-5 h-5" />
                                                            <input
                                                                type="text"
                                                                id="name"
                                                                name="name"
                                                                value={formData.name}
                                                                onChange={handleInputChange}
                                                                className="pl-10 pr-4 py-2 w-full bg-white rounded-md border border-green-700/20 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent text-gray-800"
                                                                placeholder="Your name"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div>
                                                        <label htmlFor="phone" className="block text-white text-sm font-medium mb-1">Phone Number</label>
                                                        <div className="relative">
                                                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-700 w-5 h-5" />
                                                            <input
                                                                type="tel"
                                                                id="phone"
                                                                name="phone"
                                                                value={formData.phone}
                                                                onChange={handlePhoneChange}
                                                                onFocus={handlePhoneFocus}
                                                                className="pl-10 pr-4 py-2 w-full bg-white rounded-md border border-green-700/20 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent text-gray-800"
                                                                placeholder="+44 7123 456789"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Password is auto-generated and hidden */}
                                                    <input 
                                                        type="hidden" 
                                                        name="password" 
                                                        value={formData.password} 
                                                    />
                                                    
                                                    <div className="mt-2 text-white/70 text-sm">
                                                        <p>A secure password will be automatically generated for your account.</p>
                                                        <p>You can set your own password after registration if needed.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <h2 className="text-2xl font-bold text-white mb-6">Organization Information</h2>
                                                
                                                {isLoadingSuggestions ? (
                                                    <div className="flex flex-col items-center justify-center py-6">
                                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-300 mb-4"></div>
                                                        <p className="text-white text-sm">Finding your organization...</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label htmlFor="orgName" className="block text-white text-sm font-medium mb-1">Organization Name</label>
                                                            <div className="relative">
                                                                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-700 w-5 h-5" />
                                                                <input
                                                                    type="text"
                                                                    id="orgName"
                                                                    name="orgName"
                                                                    value={formData.orgName}
                                                                    onChange={handleInputChange}
                                                                    className="pl-10 pr-4 py-2 w-full bg-white rounded-md border border-green-700/20 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent text-gray-800"
                                                                    placeholder="Your organization name"
                                                                    required
                                                                />
                                                            </div>
                                                            
                                                            {/* Organization name suggestions */}
                                                            {suggestedOrgs.length > 0 && suggestedOrgs.filter(org => org !== formData.orgName).length > 0 && (
                                                                <div className="mt-2">
                                                                    <p className="text-white text-xs mb-1">Organization names:</p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {suggestedOrgs
                                                                            .filter(org => org !== formData.orgName) // Only show suggestions different from current value
                                                                            .map((org, index) => (
                                                                                <button
                                                                                    key={index}
                                                                                    type="button"
                                                                                    onClick={() => handleOrgSuggestionClick(org)}
                                                                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                                                                                        ${formData.orgName === org 
                                                                                            ? 'bg-yellow-500 text-green-900' 
                                                                                            : 'bg-white/20 text-white hover:bg-white/30'
                                                                                        }`}
                                                                                >
                                                                                    {org}
                                                                                </button>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div>
                                                            <label htmlFor="website" className="block text-white text-sm font-medium mb-1">Website URL</label>
                                                            <div className="relative">
                                                                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-700 w-5 h-5" />
                                                                <input
                                                                    type="url"
                                                                    id="website"
                                                                    name="website"
                                                                    value={formData.website}
                                                                    onChange={handleInputChange}
                                                                    className="pl-10 pr-4 py-2 w-full bg-white rounded-md border border-green-700/20 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent text-gray-800"
                                                                    placeholder="https://www.example.com"
                                                                    required
                                                                />
                                                            </div>
                                                            
                                                            {/* Website suggestions */}
                                                            {suggestedWebsites.length > 0 && suggestedWebsites.filter(website => website !== formData.website).length > 0 && (
                                                                <div className="mt-2">
                                                                    <p className="text-white text-xs mb-1">Associated websites:</p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {suggestedWebsites
                                                                            .filter(website => website !== formData.website) // Only show suggestions different from current value
                                                                            .map((website, index) => (
                                                                                <button
                                                                                    key={index}
                                                                                    type="button"
                                                                                    onClick={() => handleWebsiteSuggestionClick(website)}
                                                                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                                                                                        ${formData.website === website 
                                                                                            ? 'bg-yellow-500 text-green-900' 
                                                                                            : 'bg-white/20 text-white hover:bg-white/30'
                                                                                        }`}
                                                                                >
                                                                                    {website}
                                                                                </button>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Error message */}
                                        {error && (
                                            <div className="mt-4 bg-red-500/20 text-white p-3 rounded-md">
                                                {error}
                                            </div>
                                        )}
                                        
                                        {/* Sign in text link - only on step 0 */}
                                        {currentStep === 0 && (
                                            <div className="mt-4 text-center">
                                                <p className="text-white/80 text-sm">
                                                    Already have an account?{' '}
                                                    <button 
                                                        onClick={() => navigate('/login')}
                                                        className="text-yellow-300 underline hover:text-yellow-200 font-medium"
                                                    >
                                                        Sign In
                                                    </button>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Fixed footer with navigation buttons */}
                        <div className="p-3 sm:p-4 border-t border-white/10 flex items-center justify-between bg-green-700/20">
                            {/* Back button */}
                            <div>
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="inline-flex items-center px-3 py-2 rounded-md
                                        transition-colors duration-200 text-sm font-medium
                                        text-white bg-green-700/50 hover:bg-green-700/70 
                                        focus:outline-none focus:bg-green-700/90"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    <span>{currentStep === 0 ? 'Back to Home' : 'Previous'}</span>
                                </button>
                            </div>
                            
                            {/* Steps indicator */}
                            <div className="text-white text-sm">
                                Step {currentStep + 1} of 2
                            </div>
                            
                            {/* Next/Submit button */}
                            <div>
                                <button
                                    type="button"
                                    onClick={goToNextStep}
                                    disabled={currentStep === 0 ? !isFormValid.userInfo : !isFormValid.orgInfo || isLoadingSuggestions}
                                    className="inline-flex items-center px-4 py-2 rounded-md
                                        bg-yellow-500 text-green-900 shadow-sm hover:bg-yellow-400
                                        transition-colors duration-200 text-sm font-semibold
                                        disabled:opacity-40 disabled:cursor-not-allowed
                                        focus:outline-none focus:ring-2 focus:ring-yellow-300/50 focus:ring-offset-2 focus:ring-offset-green-800"
                                >
                                    {isLoadingSuggestions ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{currentStep === 0 ? 'Next' : 'Create Organization'}</span>
                                            <CheckCircle className="w-4 h-4 ml-1" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlassyRegister; 